<template>
  <div class="produto-detalhes pt-6" v-if="produto">
    <v-container>
      <v-row>
        <!-- Coluna da galeria de imagens -->
        <v-col cols="12" md="6" class="produto-galeria">
          <v-card flat>
            <v-img
              :src="imagemAtual"
              :alt="produto.nome"
              height="400"
              class="rounded mb-4"
              contain
            ></v-img>
            
            <div class="d-flex miniaturas">
              <v-img
                v-for="(imagem, index) in produto.imagens" 
                :key="index"
                :src="imagem"
                @click="imagemAtual = imagem"
                height="80"
                width="80"
                class="mr-2 cursor-pointer rounded"
                :class="{ 'miniatura-ativa': imagemAtual === imagem }"
                contain
                cover
              ></v-img>
            </div>
          </v-card>
        </v-col>
        
        <!-- Coluna das informações do produto -->
        <v-col cols="12" md="6" class="produto-info">
          <h1 class="text-h4 mb-2">{{ produto.nome }}</h1>
          <v-chip class="mb-4" color="primary" label text-color="white">{{ produto.categoria }}</v-chip>
          
          <p class="text-body-1 mb-4">{{ produto.descricao }}</p>
          
          <!-- Seleção de Cor -->
          <div class="mb-6">
            <h3 class="text-h6 mb-2">Cor:</h3>
            <div class="d-flex flex-wrap">
              <v-btn
                v-for="cor in produto.cores" 
                :key="cor.id"
                class="mr-2 mb-2"
                :style="{ backgroundColor: cor.hexadecimal }"
                :class="{ 'v-btn--selected': corSelecionada === cor.id }"
                @click="corSelecionada = cor.id"
                width="40"
                height="40"
                min-width="40"
                icon
              >
                <v-icon v-if="corSelecionada === cor.id" color="white">mdi-check</v-icon>
              </v-btn>
            </div>
            <p class="text-body-2 mt-1">Cor selecionada: {{ getCorNome(corSelecionada) }}</p>
          </div>
          
          <!-- Seleção de Tamanho -->
          <div class="mb-6">
            <h3 class="text-h6 mb-2">Tamanho:</h3>
            <div class="d-flex flex-wrap">
              <v-btn
                v-for="tamanho in produto.tamanhos" 
                :key="tamanho.id"
                class="mr-2 mb-2"
                :variant="tamanhoSelecionado === tamanho.id ? 'flat' : 'outlined'"
                color="primary"
                @click="selecionarTamanho(tamanho.id)"
              >
                {{ tamanho.nome }}
              </v-btn>
            </div>
          </div>
          
          <!-- Tabela de Preços -->
          <div class="mb-6">
            <h3 class="text-h6 mb-2">Preços por quantidade:</h3>
            <v-table class="price-table">
              <thead>
                <tr>
                  <th>Quantidade</th>
                  <th>Preço unitário</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="preco in precosFiltrados" :key="preco.id">
                  <td>{{ preco.quantidade }}+</td>
                  <td class="text-right">R$ {{ preco.valor.toFixed(2) }}</td>
                </tr>
              </tbody>
            </v-table>
          </div>
          
          <!-- Controles de Compra -->
          <div class="mb-6">
            <h3 class="text-h6 mb-2">Resumo do pedido:</h3>
            <v-card class="pa-4 resumo-pedido" variant="outlined">
              <div class="d-flex align-center justify-space-between mb-2">
                <div class="text-subtitle-1">Preço unitário:</div>
                <div class="text-subtitle-1">R$ {{ precoUnitario.toFixed(2) }}</div>
              </div>
              
              <div class="d-flex align-center justify-space-between">
                <!-- Campo de quantidade com botões melhorados -->
                <div class="quantidade-wrapper">
                  <div class="quantidade-label mb-1">Quantidade:</div>
                  <div class="quantidade-controle d-flex align-center">
                    <v-tooltip text="Diminuir" location="top">
                      <template v-slot:activator="{ props }">
                        <v-btn
                          v-bind="props"
                          icon
                          density="comfortable"
                          variant="elevated"
                          color="grey-lighten-2"
                          class="quantidade-btn"
                          @click="diminuirQuantidade"
                          :disabled="quantidade <= 1"
                        >
                          <v-icon>mdi-minus</v-icon>
                        </v-btn>
                      </template>
                    </v-tooltip>
                    
                    <v-text-field
                      v-model="quantidade"
                      type="text"
                      class="quantidade-input mx-2"
                      style="width: 80px"
                      hide-details
                      density="compact"
                      bg-color="grey-lighten-3"
                      variant="outlined"
                      @change="validarQuantidade"
                      text-align="center"
                    ></v-text-field>
                    
                    <v-tooltip text="Aumentar" location="top">
                      <template v-slot:activator="{ props }">
                        <v-btn
                          v-bind="props"
                          icon
                          density="comfortable"
                          variant="elevated"
                          color="primary"
                          class="quantidade-btn"
                          @click="aumentarQuantidade"
                        >
                          <v-icon>mdi-plus</v-icon>
                        </v-btn>
                      </template>
                    </v-tooltip>
                  </div>
                </div>
                
                <div class="text-h6 font-weight-bold">
                  Total: R$ {{ totalPedido.toFixed(2) }}
                </div>
              </div>
            </v-card>
          </div>
          
          <div class="d-flex justify-space-between align-center">
            <v-btn
              color="primary"
              size="large"
              prepend-icon="mdi-cart-plus"
              @click="adicionarAoCarrinho"
              :disabled="!corSelecionada || !tamanhoSelecionado"
              class="pl-4 pr-4"
              :loading="adicionando"
              block
            >
              Adicionar ao carrinho
            </v-btn>
          </div>
          
          <!-- Mensagem de erro/alerta para seleções obrigatórias -->
          <v-alert
            v-if="!corSelecionada || !tamanhoSelecionado"
            type="info"
            class="mt-4"
            density="compact"
          >
            Por favor, selecione uma cor e um tamanho para continuar.
          </v-alert>
          
          <!-- Snackbar para feedback -->
          <v-snackbar
            v-model="snackbar.show"
            :color="snackbar.color"
            timeout="3000"
            location="top"
          >
            {{ snackbar.text }}
            
            <template v-slot:actions>
              <v-btn
                variant="text"
                @click="snackbar.show = false"
              >
                Fechar
              </v-btn>
            </template>
          </v-snackbar>
        </v-col>
      </v-row>
    </v-container>
  </div>
  <div v-else class="erro text-center pa-6 pt-16">
    <v-alert type="error">Produto não encontrado</v-alert>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { produtos } from '@/data/produtos'

export default {
  setup() {
    const route = useRoute()
    const store = useStore()
    const quantidade = ref(1)
    const corSelecionada = ref('')
    const tamanhoSelecionado = ref('')
    const produtoId = computed(() => parseInt(route.params.id))
    const adicionando = ref(false)
    const snackbar = ref({
      show: false,
      text: '',
      color: 'success'
    })
    
    const produto = computed(() => {
      return produtos.find(p => p.id === produtoId.value)
    })
    
    const imagemAtual = ref('')
    
    // Preços filtrados pelo tamanho selecionado
    const precosFiltrados = computed(() => {
      if (!produto.value || !tamanhoSelecionado.value) return []
      
      const tamanhoNome = getTamanhoNome(tamanhoSelecionado.value)
      return produto.value.precos.filter(preco => preco.tamanho === tamanhoNome)
    })
    
    // Preço unitário com base na quantidade e tamanho selecionado
    const precoUnitario = computed(() => {
      if (!produto.value || !tamanhoSelecionado.value) return 0
      
      const tamanhoNome = getTamanhoNome(tamanhoSelecionado.value)
      const precosFiltrados = produto.value.precos.filter(p => p.tamanho === tamanhoNome)
      
      // Encontra o preço apropriado para a quantidade
      for (let i = precosFiltrados.length - 1; i >= 0; i--) {
        if (quantidade.value >= precosFiltrados[i].quantidade) {
          return precosFiltrados[i].valor
        }
      }
      
      // Se não encontrar um preço para a quantidade, retorna o primeiro preço (quantidade mínima)
      return precosFiltrados.length > 0 ? precosFiltrados[0].valor : 0
    })
    
    // Total do pedido
    const totalPedido = computed(() => {
      return precoUnitario.value * quantidade.value
    })
    
    // Método para selecionar tamanho
    const selecionarTamanho = (tamanhoId) => {
      tamanhoSelecionado.value = tamanhoId
      // Resetar quantidade para 1 quando mudar de tamanho
      quantidade.value = 1
    }
    
    // Observar mudanças na quantidade para garantir valor válido
    watch(quantidade, (newValue) => {
      if (newValue < 1) {
        quantidade.value = 1
      }
    })
    
    // Métodos para aumentar e diminuir a quantidade
    const aumentarQuantidade = () => {
      quantidade.value = parseInt(quantidade.value) + 1
    }
    
    const diminuirQuantidade = () => {
      if (quantidade.value > 1) {
        quantidade.value = parseInt(quantidade.value) - 1
      }
    }
    
    const validarQuantidade = () => {
      // Converter para número
      quantidade.value = parseInt(quantidade.value) || 1
      // Garantir que seja pelo menos 1
      if (quantidade.value < 1) {
        quantidade.value = 1
      }
    }
    
    onMounted(() => {
      if (produto.value) {
        // Define valores iniciais
        imagemAtual.value = produto.value.imagens[0]
        
        // Define a primeira cor como padrão
        if (produto.value.cores && produto.value.cores.length > 0) {
          corSelecionada.value = produto.value.cores[0].id
        }
        
        // Define o primeiro tamanho como padrão
        if (produto.value.tamanhos && produto.value.tamanhos.length > 0) {
          tamanhoSelecionado.value = produto.value.tamanhos[0].id
        }
      }
    })
    
    const getCorNome = (corId) => {
      const cor = produto.value.cores.find(c => c.id === corId)
      return cor ? cor.nome : ''
    }
    
    const getCorHexadecimal = (corId) => {
      const cor = produto.value.cores.find(c => c.id === corId)
      return cor ? cor.hexadecimal : ''
    }
    
    const getTamanhoNome = (tamanhoId) => {
      const tamanho = produto.value.tamanhos.find(t => t.id === tamanhoId)
      return tamanho ? tamanho.nome : ''
    }
    
    const showSnackbar = (text, color = 'success') => {
      snackbar.value = {
        show: true,
        text,
        color
      }
    }
    
    const adicionarAoCarrinho = () => {
      if (!corSelecionada.value || !tamanhoSelecionado.value) {
        return // Validação impede o clique do botão, mas por segurança mantemos isso
      }
      
      adicionando.value = true
      
      // Simulando um pequeno delay para feedback visual
      setTimeout(() => {
        store.commit('adicionarAoCarrinho', {
          produto: produto.value,
          quantidade: quantidade.value,
          precoUnitario: precoUnitario.value,
          cor: {
            id: corSelecionada.value,
            nome: getCorNome(corSelecionada.value),
            hexadecimal: getCorHexadecimal(corSelecionada.value)
          },
          tamanho: {
            id: tamanhoSelecionado.value,
            nome: getTamanhoNome(tamanhoSelecionado.value)
          }
        })
        
        adicionando.value = false
        showSnackbar(`${produto.value.nome} adicionado ao carrinho!`)
      }, 600)
    }
    
    return {
      produto,
      quantidade,
      imagemAtual,
      corSelecionada,
      tamanhoSelecionado,
      precoUnitario,
      totalPedido,
      precosFiltrados,
      adicionando,
      snackbar,
      getCorNome,
      getCorHexadecimal,
      getTamanhoNome,
      adicionarAoCarrinho,
      aumentarQuantidade,
      diminuirQuantidade,
      validarQuantidade,
      selecionarTamanho
    }
  }
}
</script>

<style scoped>
.produto-detalhes {
  padding-top: 80px; /* Adiciona espaço para o navbar */
}

.produto-galeria {
  transition: all 0.3s ease;
}

.miniaturas {
  overflow-x: auto;
  display: flex;
  padding: 10px 0;
}

.cursor-pointer {
  cursor: pointer;
}

.miniatura-ativa {
  border: 2px solid var(--v-primary-base);
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.v-btn--selected {
  border: 2px solid white;
  box-shadow: 0 0 0 2px black;
}

.v-img:hover {
  transform: scale(1.02);
  transition: transform 0.3s ease;
}

.price-table {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}

.resumo-pedido {
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
}

.quantidade-wrapper {
  display: flex;
  flex-direction: column;
}

.quantidade-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.7);
}

.quantidade-controle {
  border-radius: 8px;
  overflow: hidden;
}

.quantidade-btn {
  transition: all 0.2s ease;
}

.quantidade-btn:hover {
  transform: scale(1.05);
}

.quantidade-input :deep(input) {
  text-align: center !important;
  font-weight: 500;
  /* Remove os botões padrão do campo number */
  -moz-appearance: textfield;
}

/* Remove os botões spinner do Chrome, Safari, Edge, Opera */
.quantidade-input :deep(input::-webkit-outer-spin-button),
.quantidade-input :deep(input::-webkit-inner-spin-button) {
  -webkit-appearance: none;
  margin: 0;
}

/* Media query para ajustar o padding em telas menores */
@media (max-width: 600px) {
  .produto-detalhes {
    padding-top: 64px; /* Ajusta para navbars menores em dispositivos móveis */
  }
}
</style>